import { SupplierNation } from '@utils/enum/SupplierNation';
import { SupplierTypesTag } from '@utils/enum/SupplierTypesTag';
import { Address, Contact, FieldsChecked, Form, PaymentMethod, PhoneNumber, SupplierForm } from 'types/formData';
import { SupplierFormObjectValidator } from 'types/formElement';

/** Italian B&B suppliers can register with a tax ID alone. */
export const isSupplierVatRequired = (nation?: string, supplierTag?: string): boolean =>
    !(nation === SupplierNation.ITALY && supplierTag === SupplierTypesTag.BED_AND_BREAKFAST);

/** Accepts only validated identifiers required for the supplier's nation. */
export const isSupplierTaxValidationSuccessful = (nation: string, supplierTag: string | undefined, fields: FieldsChecked): boolean => {
    if (nation === SupplierNation.ITALY && supplierTag === SupplierTypesTag.BED_AND_BREAKFAST) {
        return fields.taxIdCode === true && (fields.vat === true || fields.vat === null);
    }
    return fields.vat === true && (fields.taxIdCode === true || fields.taxIdCode === null);
};

const voidForm = (validator: SupplierFormObjectValidator): Form => ({
    entityType: validator.entityType,
    entityTarget: validator.entityTarget,
    companyName: '',
    vat: '',
    taxIdCode: '',
    legalAddress: voidAddressObject,
    contact: voidContactObject(validator),
    supplierNation: '',
    countryTax: '',
});

const voidAddressObject: Address = {
    address: '',
    buildingNumber: '',
    city: '',
    zip: '',
    province: '',
    country: '',
};

const voidContactObject = (validator: SupplierFormObjectValidator): Contact => ({
    phoneNumber: validator.hasPhone ? voidPhoneNumberObject : null,
    email: validator.hasEmail ? '' : null,
    accountingDocsEmail: validator.hasAccountingEmail ? '' : null,
    managerEmail: validator.hasManagerEmail ? '' : null,
    pecCompany: validator.hasPecCompany ? '' : null,
});

const voidPhoneNumberObject: PhoneNumber = {
    prefix: '',
    number: '',
};

const voidPaymentData: PaymentMethod = {
    paymentMethodId: -999,
    paymentData: null,
    countryCode: '',
    currencyCode: '',
};

export const validatorHotel = (entityType: number, entityTarget: number): SupplierFormObjectValidator => ({
    entityType: entityType,
    entityTarget: entityTarget,
    hasEmail: true,
    hasPhone: true,
    hasCommonName: true,
    hasOperationalAddress: true,
});

export const validatorBeb = (entityType: number, entityTarget: number): SupplierFormObjectValidator => ({
    entityType: entityType,
    entityTarget: entityTarget,
    hasEmail: true,
    hasPhone: true,
    hasCommonName: true,
});

export const validatorRent = (entityType: number, entityTarget: number): SupplierFormObjectValidator => ({
    entityType: entityType,
    entityTarget: entityTarget,
    hasCommonName: true,
});

export const validatorFlightCompany = (entityType: number, entityTarget: number): SupplierFormObjectValidator => ({
    entityType: entityType,
    entityTarget: entityTarget,
    hasIcao: true,
    hasCompanyCode: true,
});

export const validatorShipCompany = (entityType: number, entityTarget: number): SupplierFormObjectValidator => ({
    entityType: entityType,
    entityTarget: entityTarget,
    hasPhone: true,
    hasCompanyCode: true,
});

export const validatorTrainCompany = (entityType: number, entityTarget: number): SupplierFormObjectValidator => ({
    entityType: entityType,
    entityTarget: entityTarget,
    hasCompanyCode: true,
});

export const validatorOrdinary = (entityType: number, entityTarget: number): SupplierFormObjectValidator => ({
    entityType: entityType,
    entityTarget: entityTarget,
});

export const validatorTourOperator = (entityType: number, entityTarget: number): SupplierFormObjectValidator => ({
    entityType: entityType,
    entityTarget: entityTarget,
    hasPhone: true,
});

export const validatorCollaborators = (entityType: number, entityTarget: number): SupplierFormObjectValidator => ({
    entityType: entityType,
    entityTarget: entityTarget,
    hasAccountingEmail: true,
    hasInvoiceWorker: true,
    hasCommonName: true,
    hasBirthDate: true,
    hasBirthPlace: true,
});

export const voidSupplierForm = (validator: SupplierFormObjectValidator): SupplierForm => ({
    ...voidForm(validator),
    entityType: validator.entityType,
    entityTarget: validator.entityTarget,
    commonName: validator.hasCommonName ? '' : null,
    operationalAddress: validator.hasOperationalAddress ? voidAddressObject : null,
    birthDate: validator.hasBirthDate ? '' : null,
    birthPlace: validator.hasBirthPlace ? '' : null,
    icao: validator.hasIcao ? '' : null,
    companyCode: validator.hasCompanyCode ? '' : null,
    countryCode: '',
    paymentMethod: voidPaymentData,
    currencyCode: '',
    invoiceWorker: validator.hasInvoiceWorker ? true : null,
    invoiceWorkerAttachment: null,
    sdi: null,
    pec: null,
    specialCustomsArrangements: false,
    applicantEmail: '',
    applicantName: '',
    applicantSurname: '',
    validations: {
        vatValidations: [],
        taxIdCodeValidations: [],
        pecValidations: [],
    },
});

const commonMandatoryData = (paymentMethod: PaymentMethod) => {
    const common = [
        'companyName',
        'legalAddress.address',
        'legalAddress.city',
        'legalAddress.zip',
        'legalAddress.province',
        'vat',
        'taxIdCode',
        'countryCode',
        'currencyCode',
        'paymentMethod.id',
        'paymentMethod.countryCode',
        'paymentMethod.currencyCode',
    ];
    if (paymentMethod.paymentMethodId === 1) {
        common.push(
            'paymentMethod.paymentData.cardNumber',
            'paymentMethod.paymentData.expirationDate',
            'paymentMethod.paymentData.cvc',
            'paymentMethod.paymentData.firstName',
            'paymentMethod.paymentData.lastName',
        );
    }
    if (paymentMethod.paymentMethodId !== -1 && paymentMethod.paymentMethodId !== 1) {
        common.push(
            'paymentMethod.paymentData.iban',
            'paymentMethod.paymentData.bankAccount',
            'paymentMethod.paymentData.swift',
        );
    }
    return common;
};

export const hotelMandatoryFields = (data: SupplierForm) => [
    ...commonMandatoryData(data.paymentMethod),
    'commonName',
    'operationalAddressAddress',
    'operationalAddressCity',
    'operationalAddressZip',
    'operationalAddressProvince',
    'contact.phoneNumberPrefix',
    'contact.phoneNumberNumber',
    'contact.email',
];
